import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../utils/firebase-config';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';

/**
 * EmailVerificationPage - Shown after signup or login when email is not verified.
 * User must verify their email before accessing the app.
 */
export default function EmailVerificationPage() {
  const { user, resendVerificationEmail, logOut } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  // Poll for email verification status
  const checkVerification = useCallback(async () => {
    if (!auth.currentUser) return;
    setChecking(true);
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Error checking verification:', err);
    }
    setChecking(false);
  }, [navigate]);

  // Auto-check every 5 seconds
  useEffect(() => {
    const interval = setInterval(checkVerification, 5000);
    return () => clearInterval(interval);
  }, [checkVerification]);

  // If no user or already verified, redirect
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    } else if (user.emailVerified) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleResend = async () => {
    setError('');
    setResent(false);
    setResending(true);
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch (err) {
      if (err.code === 'auth/too-many-requests' || err.code === 'functions/resource-exhausted') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    }
    setResending(false);
  };

  const handleSignOut = async () => {
    await logOut();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="sl-page auth-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Confirm your email' }]} />
        <div className="pageheading">Confirm your email</div>
      </div>

      <div className="auth-content borderleft">
        <div className="auth-container verify-container">
          <p className="auth-subtitle">
            We've sent a verification link to
          </p>
          <p className="verify-email">{user.email}</p>
          <p className="verify-instructions">
            Click the link in the email to activate your account. 
            Check your spam folder if you don't see it.
          </p>

          {/* Status Messages */}
          {resent && (
            <div className="auth-success">
              <span>✅</span> Verification email sent! Check your inbox.
            </div>
          )}
          {error && (
            <div className="auth-error">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Actions */}
          <div className="verify-actions">
            <button
              className="auth-submit-btn"
              onClick={checkVerification}
              disabled={checking}
            >
              {checking ? 'Checking...' : 'I\'ve verified my email'}
            </button>

            <button
              className="verify-resend-btn"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend verification email'}
            </button>

            <button
              className="verify-signout-btn"
              onClick={handleSignOut}
            >
              Sign in with a different account
            </button>
          </div>

          <p className="verify-note">
            This page will automatically redirect once your email is verified.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
